import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { ShippingLineItem, ShippingOrder } from '../../../DeShippingTableWrapper';
import { useLineItems } from '../../../hooks/useLineItems';
import { useOrderData } from '../../../hooks/useOrderData';
import { LineItemFulfillmentLocationTemplate } from '../../shared/LineItemFulfillmentLocationTemplate';
import { AcceptConfirmationDialog } from './AcceptConfirmationDialog';
import { DeclineReasonDialog } from './DeclineReasonDialog';
import { useOrderSelection } from '../../../hooks/useOrderSelection';

interface ReviewActionsFooterProps {
  orderData: ReturnType<typeof useOrderData>;
  orderSelection: ReturnType<typeof useOrderSelection>;
  lineItemsHook: ReturnType<typeof useLineItems>;
}

export const ReviewActionsFooter: React.FC<ReviewActionsFooterProps> = ({
  orderData,
  orderSelection,
  lineItemsHook,
}) => {
  const order = orderSelection.selectedOrder;
  const { 
    lineItems, 
    loading, 
    selectedLineItem, 
    setSelectedLineItem,
    acceptLineItem,
    declineLineItem,
    statusUpdating,
    statusUpdateError,
    refetch
  } = lineItemsHook;

  const toast = useRef<Toast>(null);
  
  // Dialog states
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);

  // Add isActionable logic
  const isActionable = selectedLineItem?.fulfillment_status === 'accepted_awaiting_inspection';

  // Calculate reviewed items (those with decline_reason set or potentially accepted)
  const reviewedItemsCount = lineItems.filter(item => 
    item.decline_reason !== null || item.fulfillment_status === 'accepted'
  ).length;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleAcceptItem = async () => {
    if (!selectedLineItem) return;
    
    // Check if item is actionable before proceeding
    if (!isActionable) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Cannot Accept Item',
        detail: 'This item can only be accepted when it has been assigned and is awaiting inspection.',
        life: 5000
      });
      return;
    }
    
    // Show accept confirmation dialog
    setShowAcceptDialog(true);
  };

  const handleAcceptConfirm = async () => {
    if (!selectedLineItem) return;
    
    try {
      const result = await acceptLineItem();
      
      if (result.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Item accepted successfully',
          life: 3000
        });
        setShowAcceptDialog(false);
        refetch();
        orderData.refreshSingleOrder(order?.order_id!)
        lineItemsHook.refetch();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.message,
          life: 5000
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to accept item',
        life: 5000
      });
    }
  };

  const handleAcceptCancel = () => {
    setShowAcceptDialog(false);
  };

  const handleDeclineItem = async () => {
    if (!selectedLineItem) return;
    
    // Check if item is actionable before proceeding
    if (!isActionable) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Cannot Decline Item',
        detail: 'This item can only be declined when it has been assigned and is awaiting inspection.',
        life: 5000
      });
      return;
    }
    
    // Show decline dialog instead of directly declining
    setShowDeclineDialog(true);
  };

  const handleDeclineConfirm = async (reason: string) => {
    if (!reason.trim()) return;

    try {
      const result = await declineLineItem(reason);
      
      if (result.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Item declined successfully',
          life: 3000
        });
        setShowDeclineDialog(false);
        refetch();
        orderData.refreshSingleOrder(order?.order_id!)
        lineItemsHook.refetch();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.message,
          life: 5000
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to decline item',
        life: 5000
      });
    }
  };

  const handleDeclineCancel = () => {
    setShowDeclineDialog(false);
  };

  return (
    <div 
      className="border-top-1 border-300 bg-gray-50"
      style={{ 
        height: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto 1fr',
        gridTemplateAreas: `
          "order-info item-info"
          "actions actions"
        `,
        gap: '0.5rem'
      }}
    >
      <Toast ref={toast} />
      
      {/* Order Information Section - Left */}
      <div 
        style={{ 
          gridArea: 'order-info',
          borderRight: '1px solid #dee2e6',
          borderBottom: '1px solid #dee2e6',
          padding: '0.75rem'
        }}
      >
        <div className="flex flex-column gap-2">
          <div className="flex align-items-center">
            {loading ? (
              <Skeleton width="180px" height="1rem" />
            ) : (
              <span className="text-sm text-600">
                Order: {order?.order_name}
              </span>
            )}
          </div>
          <div className="flex align-items-center">
            {loading ? (
              <Skeleton width="220px" height="1rem" />
            ) : (
              <span className="text-sm text-600">
                Customer: {order?.email}
              </span>
            )}
          </div>
          <div className="flex align-items-center">
            {loading ? (
              <Skeleton width="120px" height="1rem" />
            ) : (
              <span className="text-sm text-600">
                Total Items: {order?.total_items}
              </span>
            )}
          </div>
          <div className="flex align-items-center">
            {loading ? (
              <Skeleton width="180px" height="1rem" />
            ) : (
              <span className="text-sm text-600">
                Progress: {reviewedItemsCount} of {lineItems.length} reviewed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Current Line Item Information Section - Right */}
      <div 
        style={{ 
          gridArea: 'item-info',
          borderBottom: '1px solid #dee2e6',
          padding: '0.75rem'
        }}
      >
        {/* 1st Priority: Loading state */}
        {loading ? (
          <div className="flex flex-column gap-2">
            <Skeleton width="250px" height="1rem" />
            <Skeleton width="130px" height="1rem" />
            <Skeleton width="120px" height="1rem" />
            <Skeleton width="120px" height="1rem" />
            <Skeleton width="80px" height="1rem" />
          </div>
        ) : /* 2nd Priority: No line item selected */
        !selectedLineItem ? (
          <div className="h-full flex align-items-center justify-content-center">
            <div className="text-center">
              <i className="pi pi-info-circle text-3xl text-400 mb-2"></i>
              <p className="text-500 m-0 text-sm">Select a line item to view details</p>
            </div>
          </div>
        ) : /* 3rd Priority: Show line item data */
        (
          <div className="flex flex-column gap-2">
            <div className="flex align-items-center">
              <span className="text-sm text-600">
                Current Item: {selectedLineItem.product_name}
              </span>
            </div>
            <div className="flex align-items-center">
              <span className="text-sm text-600">
                SKU: {selectedLineItem.product_sku}
              </span>
            </div>
            <div className="flex align-items-center">
              <span className="text-sm text-600">
                Qty/Price: {selectedLineItem.product_quantity} × {formatCurrency(selectedLineItem.product_price)}
              </span>
            </div>
            <div className="flex align-items-center">
              <span className="text-sm text-600">
                Assigned To: <LineItemFulfillmentLocationTemplate {...selectedLineItem} />
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons Section - Bottom Full Width */}
      <div 
        style={{ 
          gridArea: 'actions',
          padding: '0.75rem',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start'
        }}
      >
        {/* Error Message */}
        {statusUpdateError && (
          <div className="mb-2">
            <Message 
              severity="error" 
              text={statusUpdateError} 
              className="w-full"
            />
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            label="Decline Item"
            icon="pi pi-times"
            className="p-button-danger"
            disabled={loading || !selectedLineItem || statusUpdating}
            loading={statusUpdating}
            size="small"
            onClick={handleDeclineItem}
            style={{ marginRight: '1rem' }}
          />
          <Button
            label="Accept Item"
            icon="pi pi-check"
            className="p-button-success"
            disabled={loading || !selectedLineItem || statusUpdating}
            loading={statusUpdating}
            size="small"
            onClick={handleAcceptItem}
          />
        </div>
      </div>

      {/* Dialog Components */}
      <AcceptConfirmationDialog
        visible={showAcceptDialog}
        onHide={handleAcceptCancel}
        onConfirm={handleAcceptConfirm}
        selectedLineItem={selectedLineItem}
        acceptType="single"
        loading={statusUpdating}
      />

      <DeclineReasonDialog
        visible={showDeclineDialog}
        onHide={handleDeclineCancel}
        onConfirm={handleDeclineConfirm}
        selectedLineItem={selectedLineItem}
        declineType="single"
        loading={statusUpdating}
      />
    </div>
  );
};